import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSettings, updateSettings } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function SettingsPage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Settings';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [msg, setMsg]                     = useState('');
  const [autoCashout, setAutoCashout]     = useState(false);
  const [cashoutSchedule, setCashoutSchedule] = useState('weekly');
  const [cashoutMin, setCashoutMin]       = useState('10');
  const [autoStackPct, setAutoSavePct]     = useState(0);

  useEffect(() => {
    getSettings()
      .then(r => {
        setAutoCashout(r.data.autoCashout?.enabled || false);
        setCashoutSchedule(r.data.autoCashout?.schedule || 'weekly');
        setCashoutMin(((r.data.autoCashout?.minCents || 1000) / 100).toFixed(0));
        setAutoSavePct(Math.round((r.data.autoStackBps || 0) / 100));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    setSaving(true); setMsg('');
    try {
      await updateSettings({
        autoCashoutEnabled:   autoCashout,
        autoCashoutSchedule:  cashoutSchedule,
        autoCashoutMinCents:  Math.round(parseFloat(cashoutMin) * 100),
        autoStackBps:          autoStackPct * 100
      });
      setMsg('Settings saved');
    } catch (err) {
      setMsg('Failed: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  }

  if (loading) return <div style={s.loading}>Loading...</div>;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate('/dashboard')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <span style={s.headerTitle}>Settings</span>
        <div style={{width:36}}/>
      </div>

      <div style={s.body}>

        {/* Profile */}
        <div style={s.sectionTitle}>Account</div>
        <div style={s.card}>
          <div style={s.profileRow}>
            <div style={s.avatar}>
              {user?.name?.split(' ').map(n=>n[0]).join('').toUpperCase().slice(0,2)}
            </div>
            <div style={s.profileInfo}>
              <div style={s.profileName}>{user?.name}</div>
              <div style={s.profileEmail}>{user?.email || user?.phone}</div>
              <div style={s.profileCountry}>{user?.country}</div>
            </div>
          </div>
        </div>

        {/* Quick links */}
        <div style={s.sectionTitle}>Account Settings</div>
        <div style={s.card}>
          <button style={s.linkRow} onClick={() => navigate('/banks')}>
            <span style={s.linkIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="1.8" strokeLinecap="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <path d="M9 22V12h6v10"/>
              </svg>
            </span>
            <span style={s.linkLabel}>Bank Connections</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#AEAEB2" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
          <button style={{...s.linkRow, borderBottom:'none'}} onClick={() => navigate('/cashout')}>
            <span style={s.linkIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007AFF" strokeWidth="1.8" strokeLinecap="round">
                <rect x="2" y="6" width="20" height="12" rx="2"/>
                <circle cx="12" cy="12" r="3"/>
                <path d="M6 12h.01M18 12h.01"/>
              </svg>
            </span>
            <span style={s.linkLabel}>Cash Out</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#AEAEB2" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        </div>

        {/* Auto save */}
        <div style={s.sectionTitle}>Auto-Earn</div>
        <div style={s.card}>
          <div style={s.settingRow}>
            <div style={s.settingInfo}>
              <div style={s.settingLabel}>Earning on every received payment</div>
              <div style={s.settingValue}>{autoStackPct}% of every payment automatically added to your Earning balance</div>
            </div>
            <div style={s.autoStackValue}>{autoStackPct}%</div>
          </div>
          <input type="range" min="0" max="100" step="5" value={autoStackPct}
            style={s.slider}
            onChange={e => setAutoSavePct(parseInt(e.target.value))}/>
          <div style={s.sliderLabels}>
            <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
          </div>
          <div style={s.splitBar}>
            <div style={{...s.splitSpend, flex: 100-autoStackPct}}>
              {100-autoStackPct > 15 && <span>Spend {100-autoStackPct}%</span>}
            </div>
            <div style={{...s.splitSave, flex: autoStackPct}}>
              {autoStackPct > 15 && <span>Earnings {autoStackPct}%</span>}
            </div>
          </div>
        </div>

        {/* Auto cashout */}
        <div style={s.sectionTitle}>Auto cashout</div>
        <div style={s.card}>
          <div style={s.toggleRow}>
            <div style={s.settingInfo}>
              <div style={s.settingLabel}>Automatically cash out</div>
              <div style={s.settingSubLabel}>Send Spend balance to your primary bank account</div>
            </div>
            <button
              style={{...s.toggle, ...(autoCashout ? s.toggleOn : {})}}
              onClick={() => setAutoCashout(!autoCashout)}>
              <div style={{...s.toggleThumb, ...(autoCashout ? s.toggleThumbOn : {})}}/>
            </button>
          </div>

          {autoCashout && (
            <>
              <div style={s.divider}/>
              <div style={s.settingRow}>
                <div style={s.settingLabel}>Schedule</div>
                <select style={s.select} value={cashoutSchedule}
                  onChange={e => setCashoutSchedule(e.target.value)}>
                  <option value="daily">Every day</option>
                  <option value="weekly">Every Monday</option>
                  <option value="monthly">1st of month</option>
                </select>
              </div>
              <div style={s.settingRow}>
                <div style={s.settingLabel}>Minimum amount</div>
                <div style={s.minInput}>
                  <span style={s.minDollar}>$</span>
                  <input style={s.minAmount} type="number" min="1" step="1"
                    value={cashoutMin} onChange={e => setCashoutMin(e.target.value)}/>
                </div>
              </div>
              <div style={s.notice}>
                Uses your primary bank account. Runs at 9am UTC on schedule.
              </div>
            </>
          )}
        </div>

        {msg && (
          <div style={{...s.msgBox, ...(msg.includes('Failed') ? s.msgError : s.msgSuccess)}}>
            {msg}
          </div>
        )}

        <button style={s.btn} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save settings'}
        </button>

      </div>
    </div>
  );
}

const s = {
  page:          {minHeight:'100vh', background:'#F5F5F7', paddingBottom:88},
  loading:       {textAlign:'center', padding:48, color:'#6E6E73'},
  header:        {display:'flex', alignItems:'center', justifyContent:'space-between', padding:'56px 20px 16px'},
  back:          {width:36, height:36, borderRadius:10, background:'#fff', border:'none', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.08)'},
  headerTitle:   {fontSize:17, fontWeight:600, color:'#1D1D1F'},
  body:          {padding:'0 16px'},
  sectionTitle:  {fontSize:12, fontWeight:600, color:'#AEAEB2', marginBottom:8, textTransform:'uppercase', letterSpacing:0.8},
  card:          {background:'#fff', borderRadius:20, padding:20, marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  profileRow:    {display:'flex', alignItems:'center', gap:14},
  avatar:        {width:48, height:48, borderRadius:'50%', background:'#007AFF', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:600, flexShrink:0},
  profileInfo:   {flex:1},
  profileName:   {fontSize:16, fontWeight:600},
  profileEmail:  {fontSize:13, color:'#6E6E73', marginTop:2},
  profileCountry:{fontSize:12, color:'#AEAEB2', marginTop:2},
  linkRow:       {display:'flex', alignItems:'center', gap:14, width:'100%', padding:'12px 0', background:'none', border:'none', borderBottom:'1px solid #F5F5F7', cursor:'pointer', textAlign:'left'},
  linkIcon:      {fontSize:20, width:28, textAlign:'center'},
  linkLabel:     {flex:1, fontSize:15, fontWeight:500, color:'#1D1D1F'},
  settingRow:    {display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0'},
  settingInfo:   {flex:1},
  settingLabel:  {fontSize:14, fontWeight:500},
  settingSubLabel:{fontSize:12, color:'#6E6E73', marginTop:2},
  settingValue:  {fontSize:12, color:'#6E6E73', marginTop:2},
  autoStackValue: {fontSize:22, fontWeight:700, color:'#007AFF'},
  slider:        {width:'100%', accentColor:'#007AFF', marginTop:12, marginBottom:6},
  sliderLabels:  {display:'flex', justifyContent:'space-between', fontSize:10, color:'#AEAEB2', marginBottom:10},
  splitBar:      {display:'flex', height:28, borderRadius:8, overflow:'hidden', gap:2},
  splitSpend:    {background:'#F5F5F7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:600, color:'#6E6E73', transition:'flex 0.2s', minWidth:0, overflow:'hidden'},
  splitSave:     {background:'#007AFF', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:600, color:'#fff', transition:'flex 0.2s', minWidth:0, overflow:'hidden'},
  toggleRow:     {display:'flex', justifyContent:'space-between', alignItems:'center'},
  toggle:        {width:44, height:26, borderRadius:13, background:'#E5E5EA', border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0},
  toggleOn:      {background:'#007AFF'},
  toggleThumb:   {position:'absolute', top:3, left:3, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 4px rgba(0,0,0,0.2)'},
  toggleThumbOn: {left:21},
  divider:       {height:1, background:'#F5F5F7', margin:'12px 0'},
  select:        {padding:'8px 12px', background:'#F5F5F7', border:'none', borderRadius:8, fontSize:13, outline:'none'},
  minInput:      {display:'flex', alignItems:'center', gap:4, background:'#F5F5F7', borderRadius:8, padding:'6px 10px'},
  minDollar:     {fontSize:14, fontWeight:600},
  minAmount:     {width:60, background:'transparent', border:'none', fontSize:14, fontWeight:600, outline:'none', textAlign:'right'},
  notice:        {background:'#EBF4FF', borderRadius:8, padding:'8px 12px', fontSize:12, color:'#007AFF', marginTop:10},
  msgBox:        {borderRadius:10, padding:'10px 14px', fontSize:13, marginBottom:12},
  msgSuccess:    {background:'#E6FAF5', color:'#00A86B'},
  msgError:      {background:'#FFF0EF', color:'#FF3B30'},
  btn:           {width:'100%', padding:16, background:'#007AFF', color:'#fff', border:'none', borderRadius:14, fontSize:16, fontWeight:600, marginBottom:12},
};
